<template>
  <div id="account-modal" class="uk-modal-container" uk-modal>
    <div class="uk-modal-dialog">
      <form id="account-form" class="uk-form-horizontal" novalidate="true" autocomplete="off" @submit.prevent="userAccount($event)">
        <button class="uk-modal-close-full uk-close-large" type="button" uk-close></button>
        <div class="uk-modal-header">
          <h2 class="uk-modal-title">{{ modalTitle }}</h2>
        </div>
        <div class="uk-modal-body">
          <div class="uk-grid uk-grid-small uk-child-width-1-2@m" uk-grid>
            <div>
              <fieldset class="uk-fieldset">
                <legend class="uk-legend">{{ $t('user.account._common.sections.account') }}</legend>
                <div class="uk-margin-small">
                  <label class="uk-form-label" for="user-account-email">{{ $t('user._common.fields.email') }}</label>
                  <div class="uk-form-controls">
                    <input id="user-account-email" type="email" name="email" class="uk-input" v-model="profile.email" data-validate="required,email" :disabled="!userIsGuest" />
                  </div>
                </div>
                <div class="uk-margin-small">
                  <label class="uk-form-label" for="user-account-password" :data-message="[userIsGuest ? '' : $t('user.account.change_password.tooltip')]">{{ $t('user._common.fields.password') }}</label>
                  <div class="uk-grid-small uk-child-width-1-2@s" uk-grid>
                    <div><input id="user-account-password" type="password" name="password" class="uk-input" v-model="profile.password" :data-validate="[userIsGuest ? 'required' : '']" /></div>
                    <div for="user-account-password_confirmation"><input class="uk-input" id="user-account-password_confirmation" type="password" name="password_confirmation" v-model="profile.password_confirmation" :data-validate="[userIsGuest ? 'required' : '','passwordMatch']" data-validate-helper="target: #user-account-password; label: [for='user-account-password_confirmation']" :placeholder="$t('user._common.fields.confirm_password')" /></div>
                  </div>
                </div>
              </fieldset>
              <fieldset class="uk-fieldset">
                <legend class="uk-legend">{{ $t('user.account._common.sections.personal') }}</legend>
                <div class="uk-margin-small">
                  <label class="uk-form-label" for="user-account-salutation">{{ $t('user.account._common.fields.salutation') }}</label>
                  <div class="uk-form-controls">
                    <select id="user-account-salutation" name="salutation" class="uk-select uk-form-width-xsmall" v-model="profile.salutation">
                      <option v-for="(salutation, code) in $t('user.account._common.salutations')" :key="code" :value="code">{{ salutation }}</option>
                    </select>
                  </div>
                </div>
                <div class="uk-margin-small">
                  <label class="uk-form-label" for="user-account-first_name">{{ $t('user.account._common.fields.first_name') }}</label>
                  <div class="uk-form-controls">
                    <input id="user-account-first_name" type="text" name="first_name" class="uk-input" data-validate="required" v-model="profile.first_name" />
                  </div>
                </div>
                <div class="uk-margin-small">
                  <label class="uk-form-label" for="user-account-last_name">{{ $t('user.account._common.fields.last_name') }}</label>
                  <div class="uk-form-controls">
                    <input id="user-account-last_name" type="text" name="last_name" class="uk-input" data-validate="required" v-model="profile.last_name" />
                  </div>
                </div>
              </fieldset>
            </div>
            <div>
              <fieldset class="uk-fieldset">
                <legend class="uk-legend">{{ $t('user.account._common.sections.organisation') }}</legend>
                <div class="uk-margin-small">
                  <label class="uk-form-label" for="user-account-organisation">{{ $t('user.account._common.fields.organisation') }}</label>
                  <div class="uk-form-controls">
                    <input id="user-account-organisation" type="text" name="organisation" class="uk-input" data-validate="required" v-model="profile.organisation" />
                  </div>
                </div>
                <div class="uk-margin-small">
                  <label class="uk-form-label" for="user-account-address">{{ $t('user.account._common.fields.address') }}</label>
                  <div class="uk-form-controls">
                    <input id="user-account-address" type="text" name="address" class="uk-input" v-model="profile.address" />
                  </div>
                </div>
                <div class="uk-margin-small">
                  <label class="uk-form-label" for="user-account-zip">{{ $t('user.account._common.fields.zip') }}</label>
                  <div class="uk-form-controls">
                    <input id="user-account-zip" type="text" name="zip" class="uk-input uk-form-width-medium" data-validate="required" v-model="profile.zip" />
                  </div>
                </div>
                <div class="uk-margin-small">
                  <label class="uk-form-label" for="user-account-city">{{ $t('user.account._common.fields.city') }}</label>
                  <div class="uk-form-controls">
                    <input id="user-account-city" type="text" name="city" class="uk-input uk-form-width-medium" data-validate="required" v-model="profile.city" />
                  </div>
                </div>
                <div class="uk-margin-small">
                  <label class="uk-form-label" for="user-account-country">{{ $t('user.account._common.fields.country') }}</label>
                  <div class="uk-form-controls">
                    <select id="user-account-country" name="country" class="uk-select uk-form-width-medium" v-model="profile.country">
                      <option v-for="(country, code) in countries" :key="code" :value="code">{{ $t(`countries.${code}`) }}</option>
                    </select>
                  </div>
                </div>
                <div class="uk-margin-small">
                  <label class="uk-form-label" for="user-account-phone">{{ $t('user.account._common.fields.phone') }}</label>
                  <div class="uk-form-controls">
                    <input id="user-account-phone" type="tel" name="phone" class="uk-input uk-form-width-medium" data-validate="phone" v-model="profile.phone" />
                  </div>
                </div>
              </fieldset>
            </div>
          </div>
          <div v-html="$t('gdpr', { opcoEmail, base_route })" class="uk-margin-medium-top"></div>
          <div v-if="userIsGuest" class="uk-margin">
            <label for="accept-terms"><input id="accept-terms" type="checkbox" data-validate="required"> {{ $t('i_accept') }} <router-link :to="{name: 'Terms'}" class="uk-modal-close">{{ $t('route.terms') }}</router-link></label>
          </div>
        </div>
        <div class="uk-modal-footer">
          <span v-if="userIsGuest">
            <button type="button" class="uk-button uk-button-primary" uk-toggle="target: #logon-modal">{{ $t('user.login.menu') }}</button>
          </span>
          <span v-else>
            <button type="button" class="uk-button uk-button-default" @click="accountDelete">{{ $t('user.account.delete.menu') }}</button>
          </span>
          <div class="uk-align-right uk-text-right">
            <button type="button" class="uk-button uk-button-default uk-margin-small-right uk-modal-close">{{ $t('buttons.cancel') }}</button>
            <button type="submit" class="uk-button uk-button-danger" :disabled="formIsBusy">{{ modalButton }}</button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
// import { qdNow } from '@/utils/qdev'

export default {
  data () {
    return {
      formIsBusy: false,
      base_route: process.env.BASE_ROUTE
    }
  },

  computed: {
    ...mapState([
      'user'
    ]),

    ...mapGetters([
      'userIsGuest',
      'opcoEmail'
    ]),

    countries () {
      let all = this.$store.state.countries
      let result = {}

      for (let code in all) {
        if (all[code].active && all[code].users) {
          result[code] = all[code]
        }
      }

      return result
    },

    // app.__vue__.$children[0].$children[0].$children[4].profile
    profile () {
      const blank = {
        email: null,
        password: null, // put it in here in order to send it to the server while create or update, it will never come from server on user profile ...
        password_confirmation: null,
        salutation: 'Mr',
        first_name: null,
        last_name: null,
        organisation: null,
        address: null,
        zip: null,
        city: null,
        country: 'be',
        phone: null,
        is_admin: false
      }

      if (this.userIsGuest) {
        return blank
      } else {
        return {
          ...blank,
          ...this.user
        }
      }
    },

    modalTitle () {
      const type = this.userIsGuest ? 'create' : 'profile'

      return this.$t(`user.account.${type}.title`)
    },

    modalButton () {
      let result = this.$t('buttons.busy')

      if (!this.formIsBusy) {
        result = this.userIsGuest ? this.$t('user.account.create.menu') : this.$t('buttons.update')
      }

      return result
    }
  },

  mounted () {
    // upon modal open - switch it into logon mode & focus first input
    uikit.util.on('#account-modal', 'show', event => {
      // only if guest
      if (this.userIsGuest) {
        setTimeout(() => {
          const input = event.target.querySelector('input:not([disabled]):not([readonly])')

          if (input) {
            input.focus()
          }
        }, 100)
      }
    })
  },

  methods: {
    userAccount (event = null) {
      const form = event ? event.target : document.getElementById('account-form')
      const _this = this

      this.$root.$emit('validateForm', form)

      if (form.querySelectorAll(':not([hidden]) > [data-error]').length === 0 && !this.formIsBusy) {
        const method = this.userIsGuest ? 'post' : 'put'

        let data = {
          user: this.profile
        }

        if (this.userIsGuest) {
          // save all guest projects under the newly created user profile
          data.projects = this.$store.getters['db/projects/query']()
            .with('contacts')
            .with('slabs.*')
            .all()
            // remove empty / blank contacts as well
            .map(project => {
              // remove calculation_pdf (long string) and keep only info that it has in order to know on the server if we need to keep it or not
              project.slabs.map(slab => {
                if (slab.calculation_pdf && slab.calculation_pdf !== 'null' && slab.calculation_pdf.length) {
                  slab.calculation_pdf = 1
                }

                return slab
              })

              return project.prepareSend
            })
        } else {
          // if profile page & password change requested, ask for current password as well
          if (this.profile.password && this.profile.password !== null && this.profile.password !== '' && (!this.profile.password_current || !this.profile.password_current.length)) {
            // ask for password
            this.reauthorize(this.$t('user.account.change_password.title'), `<p>${this.$t('user.account.change_password.description')}</p>`).then(password => {
              if (password) {
                _this.profile.password_current = password

                _this.userAccount(event)
              }
            })

            return false
          }
        }

        this.formIsBusy = true

        // if we're sending projects to the server, let's mark them as saved now, before server response, as user might change them in the meantine
        if (this.userIsGuest && data.projects && data.projects.length) {
          // update all projects
          this.$store.dispatch('db/projects/update', {
            where () {
              return true
            },
            data: {
              // date_last_save: qdNow(), // maybe is better to wait for server response
              savedOnServer: true
            }
          })

          // allow changing slab records even if they're locked (calculation exists)
          this.$store.commit('allowChanges', true)

          // update all slabs
          this.$store.dispatch('db/slabs/update', {
            where () {
              return true
            },
            data: {
              // date_last_save: qdNow(), // maybe is better to wait for server response
              savedOnServer: true
            }
          }).then(() => {
            // switch it back
            this.$store.commit('allowChanges', false)
          })
        }

        uikit.util.ajax(`api/user`, {
          method: method,
          responseType: 'json',
          data: JSON.stringify(data),
          headers: {
            'Content-Type': 'application/json; charset=UTF-8',
            'X-Requested-With': 'XMLHttpRequest'
          }
        }).then(function (xhr) {
          const action = _this.userIsGuest ? 'create' : 'profile'

          // if success
          if (xhr.response.success) {
            // if create - log the user in (if server approved this)
            // called also on profile change in order to update user's profile state
            if (1 || action === 'create'/* && xhr.response.loggedon */) {
              // important to check if the password field is blank now
              delete _this.profile.password
              delete _this.profile.password_confirmation
              delete _this.profile.password_current

              // _this.$store.state.user = _this.profile
              _this.$store.dispatch('userLogin', _this.profile)

              // if projects & slabs data received, update date_last_save for each of them
              if (xhr.response.projects) {
                _this.$store.commit('allowChanges', true)

                xhr.response.projects.forEach(project => {
                  if (project.id && project.date_last_save) {
                    _this.$store.dispatch('db/projects/update', {
                      where: project.id,
                      data: {
                        date_last_save: project.date_last_save
                      }
                    })
                  }

                  // update each slab as well ...
                  if (project.slabs && project.slabs.length) {
                    project.slabs.forEach(slab => {
                      if (slab.id && slab.date_last_save) {
                        _this.$store.dispatch('db/slabs/update', {
                          where: slab.id,
                          data: {
                            date_last_save: slab.date_last_save
                          }
                        })
                      }
                    })
                  }
                })

                _this.$store.commit('allowChanges', false)
              }
            }

            // display message: crate or profile
            uikit.notification(`<p class="uk-h3 uk-text-success"><span class="uk-margin-small-right" uk-icon="happy"></span>${_this.$t(`user.account.${action}.success`)}</p>`, {pos: 'bottom-right', status: 'success'})

            // close modal
            uikit.modal('#account-modal').hide()
          } else {
            // insuccess
            const details = xhr.response && xhr.response.message ? xhr.response.message : _this.$t('unknown_error')

            // requet succeeded but server response failed
            uikit.notification(`<p class="uk-h3 uk-text-danger"><span class="uk-margin-small-right" uk-icon="ban"></span>${_this.$t('error_title')}</p><p>${details}</p>`, {pos: 'bottom-right', status: 'danger'})
          }

          _this.formIsBusy = false
        }, function (error) {
          const details = error.xhr.response && error.xhr.response.message ? error.xhr.response.message : _this.$t('unknown_error')

          uikit.notification(`<p class="uk-h3 uk-text-danger"><span class="uk-margin-small-right" uk-icon="ban"></span>${error.message}</p><p>${details}</p>`, {pos: 'bottom-right', status: 'danger'})

          _this.formIsBusy = false

          delete _this.profile.password
          delete _this.profile.password_confirmation
          delete _this.profile.password_current
        })
      } else {
        // fix validation errors
        uikit.notification(`<p class="uk-h3 uk-text-danger"><span class="uk-margin-small-right" uk-icon="ban"></span>${this.$t('warning_fix_errors')}</p>`, {pos: 'bottom-right', status: 'danger'})
      }
    },

    accountDelete () {
      this.reauthorize(this.$t('user.account.delete.title'), `<p>${this.$t('user.account.delete.description')}</p>`).then(password => {
        if (password && password.length) {
          let _this = this

          this.formIsBusy = true

          uikit.util.ajax(`api/user`, {
            method: 'delete',
            responseType: 'json',
            data: JSON.stringify({
              password
            }),
            headers: {
              'Content-Type': 'application/json; charset=UTF-8',
              'X-Requested-With': 'XMLHttpRequest'
            }
          }).then(function (xhr) {
            // if success
            if (xhr.response.success) {
              _this.$store.dispatch('userLogout')

              // close modal
              uikit.modal('#account-modal').hide()

              uikit.notification(`<p class="uk-h3 uk-text-success"><span class="uk-margin-small-right" uk-icon="happy"></span>${_this.$t(`user.account.delete.success`)}</p>`, {pos: 'bottom-right', status: 'success'})
            } else {
              // insuccess
              const details = xhr.response && xhr.response.message ? xhr.response.message : _this.$t('unknown_error')

              // requet succeeded but server response failed
              uikit.notification(`<p class="uk-h3 uk-text-danger"><span class="uk-margin-small-right" uk-icon="ban"></span>${_this.$t('error_title')}</p><p>${details}</p>`, {pos: 'bottom-right', status: 'danger'})
            }

            _this.formIsBusy = false
          }, function (error) {
            const details = error.xhr.response && error.xhr.response.message ? error.xhr.response.message : _this.$t('unknown_error')

            uikit.notification(`<p class="uk-h3 uk-text-danger"><span class="uk-margin-small-right" uk-icon="ban"></span>${error.message}</p><p>${details}</p>`, {pos: 'bottom-right', status: 'danger'})

            _this.formIsBusy = false
          })
        }
      })
    },

    // custom uikit.modal.prompt just to have the input type as password and adding a title as well
    // it couldn't be done perfectly altering a default uikit.modal.prompt as we don't have a reference of if ...
    reauthorize (title, message, options) {
      options = Object.assign({
        stack: true,
        bgClose: false,
        escClose: true,
        labels: uikit.modal.labels
      }, options)

      return new Promise(resolve => {
        const prompt = uikit.modal.dialog(`
          <form class="uk-form-stacked">
            <div class="uk-modal-body">
              <h3 class="uk-modal-title">${title}</h3>
              <label>${uikit.util.isString(message) ? message : uikit.util.html(message)}</label>
              <input class="uk-input" type="password" autofocus>
            </div>
            <div class="uk-modal-footer uk-text-right">
              <button class="uk-button uk-button-default uk-modal-close" type="button">${options.labels.cancel}</button>
              <button class="uk-button uk-button-primary">${options.labels.ok}</button>
            </div>
          </form>`, options)

        const input = uikit.util.$('input', prompt.$el)

        let resolved = false

        uikit.util.on(prompt.$el, 'submit', 'form', e => {
          e.preventDefault()
          resolve(input.value)
          resolved = true
          prompt.hide()
        })

        uikit.util.on(prompt.$el, 'hide', () => {
          if (!resolved) {
            resolve(null)
          }
        })
      })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/Form/userAccount.vue